// Adapts the SH-1408 wire shape to the presentational CalendarEvent, and the
// partial-failure `errors` map to per-source status. Pure + unit-tested.

import { addDays, format, startOfDay } from 'date-fns'
import type {
  CalendarEvent,
  CalendarSource,
  CalendarSubType,
  KnownCalendarSource,
  SourceStatus,
} from '../typings'
import type {
  CalendarEventDTO,
  CalendarEventsResponse,
  CalendarSourceError,
  CalendarWireSource,
} from './calendarContract'

const BUSY_TITLE = 'Busy'

const SOURCE_MAP: Record<CalendarWireSource, CalendarSource> = {
  SC: 'sales_center',
  PestPac: 'pestpac',
  Google: 'google',
  Outlook: 'outlook',
}

// SC event type -> presentational sub-type. Matched loosely so a minor backend
// label change ("NextActionDate" vs "Next Action Date") still maps.
function toSubType(eventType: string | null): CalendarSubType | undefined {
  if (!eventType) return undefined
  const t = eventType.toLowerCase()
  if (t.includes('activity')) return 'activity_reminder'
  if (t.includes('next')) return 'next_action_date'
  if (t.includes('expected') || t.includes('close'))
    return 'expected_close_date'
  return undefined
}

// AC4: the server sends UTC. Timed events keep their instant — JS renders them
// in the device's zone automatically. All-day events are date-only and must be
// zone-invariant: converting a UTC-midnight instant by zone shifts the day for
// anyone west of UTC, so we pin all-day to LOCAL midnight of the wire date
// (a no-`Z` string parses as local time). See the all-day convention in
// calendarContract.ts.
const MIDNIGHT_TIME = /T00:00:00(\.0+)?Z?$/

function localMidnight(isoDate: string): string {
  return `${isoDate}T00:00:00`
}

function allDayStart(iso: string): string {
  return localMidnight(iso.slice(0, 10))
}

// The end is exclusive (next midnight). An end carrying a time of day (e.g.
// 23:59:59 on the last day) names that day as included, so it rolls forward.
function allDayEnd(iso: string): string {
  const date = iso.slice(0, 10)
  if (MIDNIGHT_TIME.test(iso)) return localMidnight(date)
  const next = new Date(`${date}T00:00:00Z`)
  next.setUTCDate(next.getUTCDate() + 1)
  return localMidnight(next.toISOString().slice(0, 10))
}

const DUE_ONLY_BLOCK_MS = 15 * 60 * 1000
const HAS_OFFSET = /(Z|[+-]\d{2}:\d{2})$/i

type Placement = Pick<CalendarEvent, 'allDay' | 'start' | 'end'>

function toInstant(iso: string): Date {
  return new Date(HAS_OFFSET.test(iso) ? iso : `${iso}Z`)
}

function localDay(date: Date): string {
  return localMidnight(format(date, 'yyyy-MM-dd'))
}

function wirePlacement(dto: CalendarEventDTO): Placement {
  return {
    allDay: dto.isAllDay,
    start: dto.isAllDay ? allDayStart(dto.startTime) : dto.startTime,
    end: dto.isAllDay ? allDayEnd(dto.endTime) : dto.endTime,
  }
}

// The bar's end is exclusive: an end exactly at local midnight doesn't touch
// that day, any later time does.
function exclusiveEndDay(end: Date): Date {
  const day = startOfDay(end)
  return day.getTime() === end.getTime() ? day : addDays(day, 1)
}

// Activities carry full instants and the server has no viewer zone, so the
// all-day vs timed call and the bar's days are made here, in local time.
function activityPlacement(dto: CalendarEventDTO): Placement {
  const start = toInstant(dto.startTime)
  const end = toInstant(dto.endTime)
  // One malformed date must not throw out of the whole feed's .map.
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return wirePlacement(dto)
  }
  const isDueOnlyBlock =
    !dto.isAllDay && end.getTime() - start.getTime() === DUE_ONLY_BLOCK_MS
  const spansLocalDays =
    startOfDay(start).getTime() !== startOfDay(end).getTime()

  if (isDueOnlyBlock || !spansLocalDays) {
    return { allDay: false, start: dto.startTime, end: dto.endTime }
  }
  return {
    allDay: true,
    start: localDay(start),
    end: localDay(exclusiveEndDay(end)),
  }
}

export function mapCalendarEvent(dto: CalendarEventDTO): CalendarEvent {
  const source = SOURCE_MAP[dto.source] ?? dto.source
  const subType =
    source === 'sales_center' ? toSubType(dto.eventType) : undefined
  // The server already strips a busy (non-owner) event's details; re-apply it
  // here so a server regression can't leak another user's event contents.
  const isBusy = dto.visibility === 'busy'
  // Only SC events are editable — gate on source, not just the server's
  // isReadOnly flag, so a mis-set flag on an external event can't expose
  // edit/delete affordances.
  const editable = dto.source === 'SC' && !dto.isReadOnly && !isBusy

  const placement =
    dto.eventType === 'Activity' && !isBusy
      ? activityPlacement(dto)
      : wirePlacement(dto)

  return {
    // SH-2298: identity is the globally-unique sourceId, NOT dto.id — every
    // SC-derived event (Activity / NAD / ECD) ships with id=0, so keying on id
    // collapses them all to "0" (React keys, popover selection, status-override
    // map). sourceId is required and unique per event; CRUD events carry
    // sourceId = id.toString(), so their identity is unchanged.
    // Fall back to String(dto.id) if a server regression ever ships an empty/
    // missing sourceId at runtime — id must never be falsy (an empty key
    // collides React keys, drops committed statuses, and collapses the dedup).
    id: dto.sourceId || String(dto.id),
    source,
    subType,
    eventTypeId: dto.eventType ?? '',
    title: isBusy ? BUSY_TITLE : dto.title,
    description: isBusy ? undefined : (dto.description ?? undefined),
    ...placement,
    entityType: isBusy ? undefined : (dto.entityType ?? undefined),
    entityId: isBusy ? undefined : (dto.entityId ?? undefined),
    status: isBusy ? undefined : (dto.status ?? undefined),
    activityType: isBusy ? undefined : (dto.activityType ?? undefined),
    activityId: isBusy ? undefined : (dto.activityId ?? undefined),
    deepLink: isBusy ? undefined : (dto.deepLink ?? undefined),
    // String to match useActivityTypeFilter's id type (selectedIds is Set<string>).
    activityTypeId:
      isBusy || dto.activityTypeId == null
        ? undefined
        : String(dto.activityTypeId),
    hasReminder: false,
    visibility: isBusy ? 'busy' : 'full',
    permissions: { canEdit: editable, canDelete: editable },
  }
}

const KNOWN_SOURCES: KnownCalendarSource[] = [
  'sales_center',
  'pestpac',
  'google',
]

// The `errors` map may key sources with varied casing (SH-1408 grooming used
// both `salesCenter`/`pestPac` and lowercase); match case-insensitively.
// The BE's CRUD-truncation warning key, lowercased (the BE sends "calendarEvents").
// The SC data loaded, so it counts toward Sales Center but Retry can't fix it.
const TRUNCATION_ERROR_KEY = 'calendarevents'

const ERROR_ALIASES: Record<KnownCalendarSource, string[]> = {
  sales_center: ['sc', 'salescenter', 'sales_center', TRUNCATION_ERROR_KEY],
  pestpac: ['pestpac'],
  google: ['google'],
}

const TRUNCATION_KEYS: ReadonlySet<string> = new Set([TRUNCATION_ERROR_KEY])

function errorMessage(error: CalendarSourceError | undefined) {
  return typeof error === 'string' ? error : error?.message
}

/** Partial-failure map (REQ-7) -> per-source status for the UI (AC2). */
export function toSourceStatus(
  errors: CalendarEventsResponse['errors'] | undefined,
): SourceStatus[] {
  const entries = Object.entries(errors ?? {})

  const known: SourceStatus[] = KNOWN_SOURCES.map((source) => {
    const hits = entries.filter(([key]) =>
      ERROR_ALIASES[source].includes(key.toLowerCase()),
    )
    if (hits.length === 0) return { source, status: 'ok' }
    const message = hits
      .map(([, value]) => errorMessage(value))
      .filter(Boolean)
      .join(' ')
    const truncated = hits.every(([key]) =>
      TRUNCATION_KEYS.has(key.toLowerCase()),
    )
    return {
      source,
      status: 'error',
      message: message || undefined,
      ...(truncated ? { truncated: true } : {}),
    }
  })

  // Surface any errored source outside the known set (e.g. outlook) so a
  // partial failure is never silently dropped.
  const knownAliases = new Set(Object.values(ERROR_ALIASES).flat())
  const extra: SourceStatus[] = entries
    .filter(([key]) => !knownAliases.has(key.toLowerCase()))
    .map(([key, value]) => ({
      source: key.toLowerCase(),
      status: 'error',
      message: errorMessage(value),
    }))

  return [...known, ...extra]
}
